<template>
    <div v-if="!loaded" class="site-loader" :class="{ 'slide-up': slideUpTriggered }">
        <div class="site-loader__background"></div>
        <div class="site-loader__words">
            <span v-for="(char, index) in currentWordChars" :key="`${wordIndex}-${index}`" class="char">{{ char }}</span>
        </div>
        <div class="site-loader__progress">
            <div class="site-loader__progress-bar"></div>
        </div>
    </div>
    <div v-else>
        <SiteHeader />
        <main role="main">
            <router-view />
        </main>
        <SiteFooter />
    </div>
    <!-- <SiteCursor /> -->
</template>

<script>
import { gsap } from 'gsap';
import SiteHeader from './SiteHeader.vue';
import SiteFooter from './SiteFooter.vue';
// import SiteCursor from './SiteCursor.vue';

export default {
    components: {
        SiteHeader,
        SiteFooter,
        // SiteCursor,
    },
    data() {
        return {
            words: ['INTUITIVE', 'ENGAGING', 'DYNAMIC'],
            wordIndex: 0,
            slideUpTriggered: false,
            loaded: false
        };
    },
    computed: {
        currentWord() {
            return this.words[this.wordIndex] || '';
        },
        currentWordChars() {
            return this.currentWord ? this.currentWord.split('') : [];
        }
    },
    mounted() {
        // Initialize GSAP for progress bar
        gsap.set('.site-loader__progress-bar', { scaleX: 0 });

        // Animate background gradient
        gsap.to('.site-loader__background', {
            backgroundPosition: '200% 0',
            duration: 2,
            ease: 'none',
            repeat: -1
        });

        // Start showing words after a short delay to ensure DOM is ready
        this.$nextTick(() => {
            setTimeout(() => {
                this.showWords();
            }, 100);
        });
    },
    methods: {
        showWords() {
            if (this.wordIndex < this.words.length) {
                // Wait for next tick to ensure chars are rendered
                this.$nextTick(() => {
                    const chars = document.querySelectorAll('.site-loader__words .char');

                    if (chars.length === 0) {
                        // If chars not found, retry
                        setTimeout(() => this.showWords(), 50);
                        return;
                    }

                    // Reset chars for new word
                    gsap.set(chars, { opacity: 0, y: 50, rotateX: -90, scale: 0.8 });

                    // Animate chars in with stagger
                    gsap.to(chars, {
                        opacity: 1,
                        y: 0,
                        rotateX: 0,
                        scale: 1,
                        duration: 0.3,
                        stagger: 0.02,
                        ease: 'power2.out',
                        onComplete: () => {
                            setTimeout(() => {
                                // Animate chars out with different effect
                                gsap.to(chars, {
                                    opacity: 0,
                                    y: -30,
                                    rotateX: 45,
                                    scale: 0.9,
                                    duration: 0.25,
                                    stagger: 0.015,
                                    ease: 'power2.in',
                                    onComplete: () => {
                                        this.wordIndex++;

                                        // Update progress bar
                                        gsap.to('.site-loader__progress-bar', {
                                            scaleX: (this.wordIndex) / this.words.length,
                                            duration: 0.2,
                                            ease: 'power2.out'
                                        });

                                        if (this.wordIndex === this.words.length) {
                                            this.triggerSlideUp();
                                        } else {
                                            this.showWords();
                                        }
                                    }
                                });
                            }, 200);
                        }
                    });
                });
            }
        },
        triggerSlideUp() {
            this.slideUpTriggered = true;

            // Create timeline for complex exit animation
            const tl = gsap.timeline({
                onComplete: () => {
                    this.loaded = true;
                }
            });

            // Fade out progress bar
            tl.to('.site-loader__progress', {
                opacity: 0,
                duration: 0.2,
                ease: 'power2.in'
            }, 0);

            // Scale and fade background
            tl.to('.site-loader__background', {
                scale: 1.3,
                opacity: 0,
                duration: 0.4,
                ease: 'power2.in'
            }, 0.1);

            // Slide up the entire loader with bounce effect
            tl.to('.site-loader', {
                y: '-100%',
                duration: 0.6,
                ease: 'power3.inOut'
            }, 0.2);
        }
    }
}
</script>

<style lang="scss">
.site-loader {
    background-color: var(--color-black);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    overflow: hidden;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0) 50%,
            rgba(255, 255, 255, 0.03) 100%
        );
        background-size: 200% 200%;
        background-position: 0 0;
        z-index: 1;
    }

    &__words {
        font-size: 10vw;
        font-weight: 700;
        line-height: 13vw;
        text-transform: uppercase;
        color: var(--color-white);
        display: flex;
        gap: 0.5vw;
        z-index: 2;
        perspective: 1000px;

        .char {
            display: inline-block;
            transform-origin: center bottom;
            will-change: transform, opacity;
        }

        @include desktop {
            font-size: 6vw;
            line-height: 8vw;
            gap: 0.3vw;
        }
    }

    &__progress {
        position: absolute;
        bottom: rem(50px);
        left: 50%;
        transform: translateX(-50%);
        width: 80%;
        max-width: rem(400px);
        height: rem(3px);
        background-color: rgba(255, 255, 255, 0.1);
        border-radius: rem(2px);
        overflow: hidden;
        z-index: 2;

        @include desktop {
            bottom: rem(80px);
            max-width: rem(600px);
            height: rem(4px);
        }
    }

    &__progress-bar {
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.8) 0%,
            rgba(255, 255, 255, 1) 50%,
            rgba(255, 255, 255, 0.8) 100%
        );
        transform-origin: left;
        border-radius: rem(2px);
        box-shadow: 0 0 rem(10px) rgba(255, 255, 255, 0.5);
    }
}

main[role=main] {
    display: block;
    min-height: 80vh;
    @include desktop {
        min-height: calc(100vh - rem(250px));
    }
}
</style>